import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { readGoogleSettings } from "../api/google";
import { useAuth } from "../auth/auth-context";
import styles from "./home.module.css";
import { useCan } from "../auth/use-can";

/**
 * "Some chairs are not on Google yet" — STORY-130.
 *
 * ── Why Home needs this at all ──────────────────────────────────────────────
 *
 * A chair paired to Google syncs nothing until somebody confirms they can see
 * its calendar (G5): Google returns 200 for a grant to an address that does not
 * exist, so a typo looks healthy forever and only a human's eyes can catch it.
 *
 * The nag for that was one `GOOGLE_UNCONFIRMED` alert per chair, rendered in
 * *Elsewhere in the clinic* — below the fold, truncated, beside every other
 * notice, and next to a **"Mark all as done"** button. That alert is raised once
 * at pairing and never re-raised, so one click ended the only signal a
 * practice's calendars never arrived. Permanently.
 *
 * ── It is deliberately not a warning ────────────────────────────────────────
 *
 * The client's constraint, and it is the whole shape of this component:
 * **nothing may be blocked**. `googleSyncState` is read nowhere outside
 * `apps/api/src/google/`, so an unconfirmed chair books, drags and completes
 * exactly like any other — the only thing waiting is the mirror to Google. So
 * this says so in its second line rather than implying a fault, and wears the
 * accent rather than `--c-warm`, which on Home is the treatment for a real one.
 *
 * ── Practice-wide, not branch-scoped (client, 2026-09-19) ───────────────────
 *
 * `GET /google/settings` is `@ClinicScoped()`, so this counts every branch's
 * chairs. Home's own `listChairs` is branch-scoped, and building on that would
 * have re-opened the hole this exists to close: a chair added to a branch the
 * admin rarely opens would never nag. That is precisely the six-months-later
 * case G21 was written for.
 *
 * The endpoint is also `@Roles("ADMIN")`, so the role check below is not
 * belt-and-braces — it is what stops a hygienist's Home firing a request that
 * can only 403.
 */

/** How long "Remind me later" hides it for. */
const SNOOZE_DAYS = 7;

const snoozeKey = (clinicId: string): string => `dentacore.googleNudgeSnoozed.${clinicId}`;

/**
 * Is this clinic's nudge snoozed right now?
 *
 * **`localStorage`, and that is the right weakness.** A snooze is a per-viewer
 * convenience — it changes no data, and the chairs stay exactly as unconfirmed
 * as they were. It deliberately does not follow the person to another device:
 * the underlying state is what matters, and anywhere they have not dismissed it
 * they are told again.
 */
function snoozedUntil(clinicId: string): number {
  try {
    const raw = localStorage.getItem(snoozeKey(clinicId));
    if (raw === null) return 0;
    const at = Number(raw);
    return Number.isFinite(at) ? at : 0;
  } catch {
    // Private browsing, or storage disabled. Never snoozed, which is the
    // permissive branch: the person sees the nudge rather than losing it.
    return 0;
  }
}

export function GoogleNudge(): React.JSX.Element | null {
  const { user } = useAuth();
  const can = useCan();
  const [waiting, setWaiting] = useState(0);
  const [snoozed, setSnoozed] = useState(true);

  const clinicId = user?.clinicId ?? null;
  const isAdmin = can("settings.integrations.manage");

  useEffect(() => {
    if (!isAdmin || clinicId === null) return;

    setSnoozed(snoozedUntil(clinicId) > Date.now());

    const controller = new AbortController();
    readGoogleSettings(controller.signal)
      .then((settings) => {
        if (controller.signal.aborted) return;
        setWaiting(settings.chairs.filter((c) => c.state === "AWAITING_CONFIRMATION").length);
      })
      .catch(() => {
        // Silent on purpose. This is a nudge about a setup step, and a
        // dashboard that cannot reach one endpoint must not show an error
        // where the day's figures go.
      });

    return () => {
      controller.abort();
    };
  }, [isAdmin, clinicId]);

  if (!isAdmin || clinicId === null || waiting === 0 || snoozed) return null;

  return (
    <div className={styles.nudge} data-testid="google-nudge">
      <div>
        <p className={styles.nudgeTitle}>
          {waiting} chair{waiting === 1 ? "" : "s"} {waiting === 1 ? "isn" : "aren"}&rsquo;t syncing
          to Google yet
        </p>
        <p className={styles.nudgeNote}>
          Bookings are unaffected — confirm you can see the calendars to switch syncing on.
        </p>
      </div>
      <div className={styles.nudgeActions}>
        <Link className={styles.nudgeGo} to="/settings/google" data-testid="google-nudge-go">
          Finish setting up
        </Link>
        <button
          type="button"
          className={styles.nudgeLater}
          data-testid="google-nudge-later"
          onClick={() => {
            try {
              localStorage.setItem(
                snoozeKey(clinicId),
                String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000),
              );
            } catch {
              // It simply does not persist; hiding it for this render is still
              // the right answer to the click.
            }
            setSnoozed(true);
          }}
        >
          Remind me later
        </button>
      </div>
    </div>
  );
}
